"use client";

import type { StaticImageData } from "next/image";
import NextImage from "next/image";
import React from "react";
import type { Props as MediaProps } from "../types";

const cssVariables = {
  breakpoints: {
    "3xl": 1920,
    "2xl": 1536,
    xl: 1280,
    lg: 1024,
    md: 768,
    sm: 640,
  },
};

const { breakpoints } = cssVariables;

// A base64 encoded image to use as a placeholder while the image is loading
const placeholderBlur =
  "data:image/png;base64,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";

export function ImageMedia(props: MediaProps) {
  const {
    alt: altFromProps,
    fill,
    imgClassName,
    priority,
    resource,
    size: sizeFromProps,
    src: srcFromProps,
    loading: loadingFromProps,
  } = props;

  let width: number | undefined;
  let height: number | undefined;
  let alt = altFromProps;
  let src: StaticImageData | string = srcFromProps || "";

  if (!src && resource && typeof resource === "object") {
    const {
      alt: altFromResource,
      filename: fullFilename,
      height: fullHeight,
      url,
      width: fullWidth,
    } = resource;

    width = fullWidth!;
    height = fullHeight!;
    alt = altFromResource || "";

    src = `${url}`;
  }

  const loading = loadingFromProps || (!priority ? "lazy" : undefined);

  // NOTE: this is used by the browser to determine which image to download at different screen sizes
  const sizes = sizeFromProps
    ? sizeFromProps
    : Object.entries(breakpoints)
        .map(([, value]) => `(max-width: ${value}px) ${value * 2}w`)
        .join(", ");

  return (
    <picture>
      <NextImage
        alt={alt || ""}
        className={imgClassName}
        fill={fill}
        height={!fill ? height : undefined}
        placeholder="blur"
        blurDataURL={placeholderBlur}
        priority={priority}
        quality={100}
        loading={loading}
        sizes={sizes}
        src={src}
        width={!fill ? width : undefined}
      />
    </picture>
  );
}
